// src/components/tableros/tabs/TemasTab.js - Versión compatible
import React from 'react';
import {
  Grid,
  Typography,
  Stack,
  Button,
  Divider,
  Paper,
  Box,
  Tooltip
} from '@mui/material';
import { SportsMma, Speed, Insights, Stadium, SportsSoccer, 
         NightsStay, Hotel, Contrast, SportsTennis, DirectionsRun } from '@mui/icons-material';
// Importamos los temas pero mantenemos compatibilidad con props
import { themes as themesImport } from '../constants/themes'; 

const TemasTab = ({
  themes = themesImport, // Usa los temas importados como fallback
  applyTheme,
  tableBackgroundColor,
  globalFontColor,
  isMobile = false,
  showSnackbar
}) => {
  // Asegurémonos de que tenemos temas disponibles
  const availableThemes = Object.keys(themes).length > 0 ? themes : themesImport;
  
  console.log("Temas disponibles:", Object.keys(availableThemes)); // Para depuración
  
  // Organizar los temas por categorías
  const themeCategories = {
    "Básicos": ["classic", "light", "dark", "highContrast"],
    "Deportes": ["olympic", "athletics", "soccer", "tennis", "swimming"],
    "Ambientes": ["digital", "stadium", "nightMode", "modern", "retro"]
  };

  // Función para manejar la aplicación del tema
  const handleApplyTheme = (themeName) => {
    if (typeof applyTheme === 'function') {
      applyTheme(themeName);
    } else {
      console.error("La función applyTheme no está disponible");
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Encabezado */}
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Temas Predefinidos
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Selecciona un tema para aplicar automáticamente una combinación de colores diseñada profesionalmente
          a tu tablero. Puedes personalizar después los colores individuales si lo deseas.
        </Typography>
      </Grid>

      {/* Renderizar cada categoría de temas */}
      {Object.entries(themeCategories).map(([category, themeNames]) => (
        <Grid item xs={12} key={category}>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              {getCategoryIcon(category)}
              <Box sx={{ ml: 1 }}>{category}</Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={2} 
              flexWrap="wrap" 
              sx={{ 
                gap: 2 
              }}
            >
              {themeNames.map((themeName) => {
                // Verificar si el tema existe en la lista de temas disponibles
                const theme = availableThemes[themeName];
                if (!theme) {
                  console.log(`Tema no encontrado: ${themeName}`); // Para depuración
                  return null;
                }
                
                return (
                  <Tooltip 
                    key={themeName} 
                    title={getThemeDescription(themeName)}
                    arrow
                  >
                    <Button
                      variant="contained"
                      onClick={() => handleApplyTheme(themeName)}
                      sx={{
                        backgroundColor: theme.tableBackgroundColor,
                        color: theme.globalFontColor,
                        minWidth: 120,
                        mb: 2,
                        border: `1px solid ${theme.tableBackgroundColor === '#ffffff' ? '#e0e0e0' : 'transparent'}`,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundImage: `linear-gradient(45deg, ${theme.headerBackgroundColor || theme.tableBackgroundColor} 25%, transparent 25%, transparent 50%, ${theme.tableRowOddColor || theme.tableBackgroundColor} 50%, ${theme.tableRowOddColor || theme.tableBackgroundColor} 75%, transparent 75%, transparent)`,
                          backgroundSize: '10px 10px',
                          opacity: 0.1,
                          zIndex: 0
                        },
                        '&:hover': {
                          backgroundColor: theme.tableBackgroundColor,
                          opacity: 0.9,
                        },
                        '& .MuiButton-label': {
                          zIndex: 1
                        }
                      }}
                    >
                      {getThemeLabel(themeName)}
                    </Button>
                  </Tooltip>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

// Función para obtener íconos por categoría
const getCategoryIcon = (category) => {
  switch (category) {
    case "Básicos":
      return <Insights />;
    case "Deportes":
      return <DirectionsRun />;
    case "Ambientes":
      return <NightsStay />;
    default:
      return <Insights />;
  }
};

// Función para obtener íconos específicos por tema
const getThemeIcon = (themeName) => {
  switch (themeName) {
    case "olympic":
      return <SportsMma />;
    case "stadium":
      return <Stadium />;
    case "soccer":
      return <SportsSoccer />;
    case "nightMode":
      return <NightsStay />;
    case "highContrast":
      return <Contrast />;
    case "tennis":
      return <SportsTennis />;
    case "athletics":
      return <DirectionsRun />;
    case "swimming":
      return <Hotel />;
    case "digital":
      return <Speed />;
    default:
      return null;
  }
};

// Función para obtener etiquetas de temas
const getThemeLabel = (themeName) => {
  switch (themeName) {
    case "classic":
      return "Clásico";
    case "light":
      return "Claro";
    case "dark":
      return "Oscuro";
    case "olympic":
      return "Olímpico";
    case "stadium":
      return "Estadio";
    case "digital":
      return "Digital";
    case "modern":
      return "Moderno";
    case "nightMode":
      return "Nocturno";
    case "retro":
      return "Retro";
    case "highContrast":
      return "Alto Contraste";
    case "soccer":
      return "Fútbol";
    case "tennis":
      return "Tenis";
    case "athletics":
      return "Atletismo";
    case "swimming":
      return "Natación";
    default:
      return themeName.charAt(0).toUpperCase() + themeName.slice(1);
  }
};

// Función para obtener descripciones de temas
const getThemeDescription = (themeName) => {
  switch (themeName) {
    case "classic":
      return "Tema clásico de tablero con fondo oscuro y texto claro";
    case "light":
      return "Tema con fondo claro y textos oscuros para ambientes luminosos";
    case "dark":
      return "Tema oscuro para minimizar fatiga visual";
    case "olympic":
      return "Inspirado en los colores olímpicos con azul y dorado";
    case "stadium":
      return "Tema verde inspirado en campos deportivos";
    case "digital":
      return "Estilo retro de monitor digital con texto verde";
    case "modern":
      return "Diseño moderno con tonos azules";
    case "nightMode":
      return "Tema de máximo oscurecimiento para ambientes nocturnos";
    case "retro":
      return "Tonos naranja y marrón con estética retro";
    case "highContrast":
      return "Máximo contraste para mejor visibilidad";
    case "soccer":
      return "Inspirado en los colores del fútbol";
    case "tennis":
      return "Colores inspirados en las canchas de tenis";
    case "athletics":
      return "Tema inspirado en las pistas de atletismo";
    case "swimming":
      return "Tema con tonos azules inspirado en natación";
    default:
      return `Tema ${themeName}`;
  }
};

// Exportamos los temas por defecto por si se necesitan
export const defaultThemes = {
  classic: {
    tableBackgroundColor: '#1a1a1a',
    globalFontColor: '#ffffff',
    headerBackgroundColor: '#2c0369',
    headerFontColor: '#ffffff',
    columnsHeaderColor: '#4c516d',
    positionColor: '#ffffff',
    positionBackgroundColor: '#4c516d',
    nameBackgroundColor: '#515e7b',
    athleteNameColor: '#ffffff',
    teamNameColor: '#e0e0e0',
    markBackgroundColor: '#000000',
    resultTimeColor: '#ffffff',
    timerColor: '#00ff00',
    largeTimerColor: '#00ff00',
    tableRowOddColor: '#252525',
    tableRowEvenColor: '#1e1e1e',
    deltaTimeColor: '#c1ff30',
    failColor: '#b71c1c',
    passColor: '#0d47a1',
    successColor: '#1b5e20'
  },
  // Definir al menos un tema por defecto para que funcione incluso si no se importan los temas
};

export default TemasTab;